// src/components/instructor/ops/BookingDetailModal.js
//
// Pass 58A — the ONE canonical operational Booking Detail surface for the Bookings workspace. Opening a
// booking from Day / Week / Unassigned (and any other ops navigation) reaches THIS modal, which hosts the
// existing authoritative BookingDetailActions (Publish Early / Cancel). It does NOT re-implement any
// booking logic — it is a read-only summary + the mounted action component. RDO / staff time-off is
// shown as a distinct all-day "Day Off" summary with no client actions (it is not a client booking).

import React, { useState } from 'react';
import BookingDetailActions from './BookingDetailActions';
import { fmtTime } from './opsShared';
import { bookingLessonEligibility } from '../../../utils/lessonHandoff';

const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '8vh 16px' };
const panel = { background: '#fff', borderRadius: 12, width: '100%', maxWidth: 460, padding: 18, boxShadow: '0 12px 40px rgba(0,0,0,0.2)', fontFamily: "'DM Sans',sans-serif", color: '#1A1917' };
const row = { display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, padding: '3px 0' };
const label = { color: '#9E9B95' };

const fmtDay = (b) => (b.startAt ? '' : (b.serviceDate || '')); // client shows time; date-only shows serviceDate

export default function BookingDetailModal({ booking, organizationId, organization = {}, member = null, seasonId, onClose, onChanged, onOpenCase = null, onStartLesson = null, readOnly = false, locationConfig = null }) {
  if (!booking) return null;
  const timeoff = booking.activityType === 'timeoff' || booking.scheduleEntryType === 'blocked';
  const guest = booking.requesterName || booking.leadGuest?.fullName || '';
  // Pass 82E §31 — distinguish operating area / service location / meeting-or-pickup / accommodation (they answer
  // different questions; never flattened to one "Location"). Names resolved from the org config; ids never shown raw.
  const cfg = locationConfig || {};
  const nameFrom = (list, idKey, id) => (id && (list || []).find((x) => x[idKey] === id)) ? (list.find((x) => x[idKey] === id).name) : '';
  const areaName = nameFrom(cfg.areas, 'operatingAreaId', booking.operatingAreaId);
  const serviceLocationName = nameFrom(cfg.serviceLocations, 'serviceLocationId', booking.serviceLocationId);
  const meetingPointName = nameFrom(cfg.meetingPoints, 'meetingPointId', booking.meetingPointId);
  const meetingLabel = meetingPointName || booking.locationLabel || '';
  const when = booking.startAt
    ? `${fmtTime(booking.startAt)}${booking.endAt ? `–${fmtTime(booking.endAt)}` : ''}`
    : 'All day';

  const done = () => { onChanged && onChanged(); };

  return (
    <div style={overlay} data-testid="booking-detail-modal" onClick={onClose}>
      <div style={panel} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
            {timeoff ? 'Day Off (RDO)' : (booking.bookingType || booking.serviceType || 'Booking')}
          </h3>
          <button type="button" data-testid="booking-detail-close" onClick={onClose}
            style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', color: '#9E9B95' }}>×</button>
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={row}><span style={label}>Date</span><span>{booking.serviceDate || fmtDay(booking) || '—'}</span></div>
          <div style={row}><span style={label}>Time</span><span>{when}</span></div>
          {!timeoff && <div style={row}><span style={label}>Guest</span><span>{guest || '—'}</span></div>}
          <div style={row}><span style={label}>Instructor</span><span>{booking.instructorNameSnapshot || booking.sourceInstructorLabel || (booking.instructorId ? booking.instructorId : 'Unassigned')}</span></div>
          {!timeoff && areaName ? <div style={row}><span style={label}>Operating area</span><span>{areaName}</span></div> : null}
          {!timeoff && serviceLocationName ? <div style={row}><span style={label}>Service location</span><span>{serviceLocationName}</span></div> : null}
          {!timeoff && meetingLabel ? <div style={row}><span style={label}>{booking.meetingType === 'pickup' ? 'Pickup' : 'Meeting point'}</span><span>{meetingLabel}</span></div> : null}
          {!timeoff && booking.accommodationName ? <div style={row}><span style={label}>Accommodation</span><span>{booking.accommodationName}</span></div> : null}
          <div style={row}><span style={label}>Status</span><span>{booking.status || 'confirmed'}</span></div>
          {/* Pass 65 — reverse traceback: a case-linked booking can open its originating enquiry. Only shown
              when a bookingRequestId exists (never a fake case for a source/historical booking). */}
          {onOpenCase && booking.bookingRequestId ? (
            <div style={row}><span style={label}>Enquiry</span>
              <button type="button" data-testid="booking-open-case" onClick={() => onOpenCase(booking.bookingRequestId)}
                style={{ border: 'none', background: 'none', color: '#1A3FA0', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, padding: 0, textDecoration: 'underline' }}>Open enquiry</button>
            </div>
          ) : null}
        </div>

        {timeoff ? (
          <div data-testid="rdo-detail-note" style={{ fontSize: 12, color: '#6B6860', background: '#F4F3F0', borderRadius: 8, padding: '8px 10px' }}>
            Rostered day off — all-day staff activity. Blocks teaching capacity; not a client lesson (no guest, price, payment, or coaching actions).
          </div>
        ) : (
          <>
            {/* Pass 66 — booking → lesson delivery handoff. Explicit action only (never on render); creates
                exactly one deterministic booking-linked lessonInstance, then opens the existing lesson surface. */}
            {onStartLesson ? <LessonDelivery booking={booking} readOnly={readOnly} onStartLesson={onStartLesson} /> : null}
            <BookingDetailActions
              booking={booking}
              organizationId={organizationId}
              organization={organization}
              member={member}
              seasonId={seasonId}
              onDone={done}
            />
          </>
        )}
      </div>
    </div>
  );
}

function LessonDelivery({ booking, readOnly, onStartLesson }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const elig = bookingLessonEligibility(booking, { readOnly });
  // only show for a client booking (eligible now, or assigned-but-not-yet-eligible with a real reason).
  const entry = booking.scheduleEntryType || 'client_booking';
  if (entry !== 'client_booking' || booking.synthesized) return null;
  const start = async () => {
    if (busy) return; setBusy(true); setError('');
    try { await onStartLesson(booking); } catch (e) { setError(e?.message || 'Could not start the lesson.'); } finally { setBusy(false); }
  };
  return (
    <div data-testid="lesson-delivery" style={{ marginTop: 10, borderTop: '1px solid #ECEAE6', paddingTop: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#9E9B95', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Lesson delivery</div>
      {elig.eligible ? (
        <button type="button" data-testid="booking-start-lesson" disabled={busy} onClick={start}
          style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: busy ? '#9DB4F0' : '#1A1917', color: '#fff', cursor: busy ? 'default' : 'pointer', fontSize: 13, fontWeight: 600 }}>{busy ? 'Opening…' : 'Start lesson'}</button>
      ) : (
        <div data-testid="lesson-delivery-blocked" style={{ fontSize: 12.5, color: '#6B6860' }}>
          {!elig.assigned ? 'Assign an instructor before starting the lesson.'
            : elig.reasons.includes('historical_read_only') ? 'Historical — read-only.'
              : elig.reasons.includes('cancelled') ? 'Cancelled — no lesson.'
                : 'Not available for this booking.'}
        </div>
      )}
      {error ? <div role="alert" style={{ color: '#DC2626', fontSize: 12, marginTop: 6 }}>{error}</div> : null}
      <div style={{ fontSize: 11, color: '#9E9B95', marginTop: 6 }}>Opens the lesson record for this booking (created once; no duplicate on reopen).</div>
    </div>
  );
}
